import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CountriesService } from 'src/app/services/countries/countries.service';
import { CountryModalService } from 'src/app/services/country-modal/country-modal.service';
import { PortService } from 'src/app/services/port/port.service';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-country',
  templateUrl: './add-edit-country.component.html',
  styleUrls: ['./add-edit-country.component.scss']
})
export class AddEditCountryComponent extends DestructorComponent implements OnInit {

  @Output() countryAdded = new EventEmitter<void>();
  @Output() countryUpdated = new EventEmitter<void>();

  countryForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedCountry: any = null;

  // Dropdown data
  parentCountries: any[] = [];
  portList: any[] = [];

  // Inspection options
  inspectionOptions = [
    { value: 'ATJ', label: 'ATJ' },
    { value: 'BUREAU VERITAS INSPECTION', label: 'BUREAU VERITAS INSPECTION' },
    { value: 'EAA', label: 'EAA' },
    { value: 'INTERTEK', label: 'INTERTEK' },
    { value: 'JAAI', label: 'JAAI' },
    { value: 'JEVIC', label: 'JEVIC' }
  ];

  // Ship method options
  shipMethodOptions = [
    { value: 'RORO', label: 'RORO' },
    { value: 'Container', label: 'Container' }
  ];

  constructor(
    private fb: FormBuilder,
    private _countriesService: CountriesService,
    private _countryModalService: CountryModalService,
    private _portService: PortService,
    private _globalStateService: GlobalStateService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadParentCountries();
    this.loadPorts();
  }

  private initializeForm(): void {
    this.countryForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      region: [''],
      parent: [''],
      port: [''],
      inspection: [[]],
      ship_method: [[]]
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_country_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceCountry = this._countryModalService.getSelectedCountry();
        if (serviceCountry) {
          this.isEditMode = true;
          this.selectedCountry = serviceCountry;
          this.populateForm(this.selectedCountry);
        } else {
          this.isEditMode = false;
          this.selectedCountry = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._countryModalService.clearSelectedCountry();
        this.resetForm();
      });
    }
  }

  private loadParentCountries(): void {
    this.subscriptions.push(
      this._countriesService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.parentCountries = response.data.data || response.data;
          }
        },
        error: (error: any) => {
          console.error('Error loading parent countries:', error);
        }
      })
    );
  }

  private loadPorts(): void {
    this.subscriptions.push(
      this._portService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.portList = response.data.data || response.data;
          }
        },
        error: (error: any) => {
          console.error('Error loading ports:', error);
        }
      })
    );
  }

  private populateForm(country: any): void {
    this.countryForm.patchValue({
      name: country.name || '',
      region: country.region || '',
      parent: country.parent || '',
      port: country.port || '',
      inspection: country.inspection || [],
      ship_method: country.ship_method || []
    });
  }

  private resetForm(): void {
    this.countryForm.reset();
    this.countryForm.patchValue({
      inspection: [],
      ship_method: []
    });
    this.isLoading = false;
  }

  // Getter for easy access to form controls
  get name() { return this.countryForm.get('name'); }
  get region() { return this.countryForm.get('region'); }
  get parent() { return this.countryForm.get('parent'); }
  get port() { return this.countryForm.get('port'); }
  get inspection() { return this.countryForm.get('inspection'); }
  get ship_method() { return this.countryForm.get('ship_method'); }

  // Multi-select inspection methods
  toggleInspection(value: string): void {
    const currentInspections = this.countryForm.get('inspection')?.value || [];
    const index = currentInspections.indexOf(value);

    if (index > -1) {
      currentInspections.splice(index, 1);
    } else {
      currentInspections.push(value);
    }

    this.countryForm.patchValue({ inspection: currentInspections });
  }

  isInspectionSelected(value: string): boolean {
    const currentInspections = this.countryForm.get('inspection')?.value || [];
    return currentInspections.includes(value);
  }

  // Ship method checkbox methods
  toggleShipMethod(value: string): void {
    const currentMethods = this.countryForm.get('ship_method')?.value || [];
    const index = currentMethods.indexOf(value);

    if (index > -1) {
      currentMethods.splice(index, 1);
    } else {
      currentMethods.push(value);
    }

    this.countryForm.patchValue({ ship_method: currentMethods });
  }

  isShipMethodSelected(value: string): boolean {
    const currentMethods = this.countryForm.get('ship_method')?.value || [];
    return currentMethods.includes(value);
  }

  onSubmit(): void {
    if (this.countryForm.invalid) {
      this.markFormGroupTouched(this.countryForm);
      return;
    }

    this.isLoading = true;

    const formData = {
      name: this.countryForm.get('name')?.value,
      region: this.countryForm.get('region')?.value,
      parent: this.countryForm.get('parent')?.value || null,
      port: this.countryForm.get('port')?.value || null,
      inspection: this.countryForm.get('inspection')?.value || [],
      ship_method: this.countryForm.get('ship_method')?.value || []
    };

    if (this.isEditMode) {
      this.updateCountry(formData);
    } else {
      this.addCountry(formData);
    }
  }

  private addCountry(formData: any): void {
    this.subscriptions.push(
      this._countriesService.AddData(formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Country added successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.countryAdded.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add country.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error adding country:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding the country.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private updateCountry(formData: any): void {
    const countryId = this.selectedCountry._id || this.selectedCountry.id;
    this.subscriptions.push(
      this._countriesService.UpdateData(countryId, formData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Country updated successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.countryUpdated.emit();
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to update country.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error updating country:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while updating the country.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_country_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }
}
